import axios from 'axios';
import { nextEventLoop } from 'helpers/timing';
import ApplicationController from './application_controller';

export default class extends ApplicationController {
  static values = {
    src: String,
    loading: {
      type: String,
      default: 'eager',
    },
    disciplineId: String,
    patientId: String,
    dateTimeOfService: String,
  };

  controller = new AbortController();

  initialize() {
    this.intersectionObserver = new IntersectionObserver(this.intersect.bind(this));

    if (this.loadingValue === 'eager') this.makeRequest();
    if (this.loadingValue === 'lazy') this.intersectionObserver.observe(this.element);
  }

  async srcValueChanged() {
    // make request only when the oldValue was present and it changed
    const previousNote = document.getElementById('visit-note-previous-note');
    const loadingIndicator = document.getElementById('loading-indicator');

    previousNote?.classList.add('d-none');
    loadingIndicator?.classList.remove('d-none');
    await this.makeRequest();
    loadingIndicator?.classList.add('d-none');
    previousNote?.classList.remove('d-none');
  }

  loadPreviousNote(event) {
    const { id, dataset } = event.detail.relatedTarget;
    this.srcValue = `/visit-notes/${id}/previous-note?discipline_id=${this.disciplineIdValue}&patient_id=${this.patientIdValue}&crud_action=${dataset.crudaction}&model_id=${dataset.modelid}&date_time_of_service=${this.dateTimeOfServiceValue}`;
  }

  async makeRequest() {
    if (!this.hasSrcValue || !this.srcValue) return;

    this.controller?.abort();
    this.controller = new AbortController();

    this.clearObserver();
    try {
      const data = await makeNetworkRequest(this.element, this.srcValue, {
        headers: {
          accept: 'text/fragment+html',
          credentials: 'same-origin',
        },
        signal: this.controller.signal,
      });
      this.targetContainer.innerHTML = data;
      this.setPreviousNoteClickListener();
      this.dispatch('success', { bubbles: true });
      this.controller = null;
    } catch (error) {
      if (!axios.isCancel(error)) {
        this.dispatch('error', { bubbles: true });
        this.controller = null;
      }
    }
  }

  setPreviousNoteClickListener() {
    const dateField = document.getElementById('visit_note_date_time_of_service');
    const previousNoteLink = document.getElementById('use_chosen_previous_note');

    if (previousNoteLink) {
      previousNoteLink.addEventListener('click', () => {
        const datetime = dateField.value;
        const timezone = previousNoteLink.getAttribute('data-timezone');
        const datetimeWithTimezone = datetime + timezone;
        const url = new URL(previousNoteLink.href);
        url.searchParams.set('date_time_of_service', datetimeWithTimezone);
        previousNoteLink.href = url.toString();
      });
    }
  }

  // Private

  intersect(entries) {
    const lastEntry = entries.slice(-1)[0];
    if (lastEntry?.isIntersecting) {
      const { target } = lastEntry;

      this.clearObserver(target);
      this.makeRequest();
    }
  }

  clearObserver(target = this.element) {
    this.intersectionObserver?.unobserve(target);
  }

  get targetContainer() {
    const element = document.getElementById('visit-note-previous-note');
    return element || this.element;
  }
}

async function makeNetworkRequest(el, src, options) {
  try {
    el.dispatchEvent(new CustomEvent('loadstart'));
    const { data } = await axios.get(src, options);
    el.dispatchEvent(new CustomEvent('load'));
    el.dispatchEvent(new CustomEvent('loadend'));
    await nextEventLoop();
    return data;
  } catch (error) {
    el.dispatchEvent(new CustomEvent('error'));
    el.dispatchEvent(new CustomEvent('loadend'));
    await nextEventLoop();
    throw error;
  }
}
